import React, { useState } from "react";
import banner from "assets/img/profile/banner.png";
import Card from "components/card";
import { useAuth } from "context/AuthProvider";
import { useFormik } from "formik";
import * as Yup from "yup";
import { FaUser } from "react-icons/fa";
import { RiEditFill } from "react-icons/ri";
import { MdCancel, MdLogout, MdSave, MdSaveAlt } from "react-icons/md";
import { useNavigate } from "react-router-dom";

const Banner = () => {
  const { currentUser, updateCurrentUser, logout } = useAuth();
  const [editable, setEditable] = useState(false); // State to control edit mode
  const navigate = useNavigate();
  const initialValues = {
    fullname: currentUser.fullname,
    contact_no: currentUser.contact_no,
    organization_name: currentUser.organization_name,
  };

  const logoutAction = () => {
    logout();
    navigate("/");
  };

  const validationSchema = Yup.object().shape({
    fullname: Yup.string()
      .matches(/^[A-Za-z\s'-]{2,}$/, "Invalid name")
      .required("Name is required"),
    contact_no: Yup.string().required("Contact number is required"),
    organization_name: Yup.string()
      .matches(/^[A-Za-z\s'-]{3,}$/, "Invalid organization name")
      .required("Organization name is required"),
  });

  const formik = useFormik({
    initialValues,
    validationSchema,
    onSubmit: (values) => {
      // Update the user's information when saving changes
      // Check if the name is changed before updating
      if (
        values.fullname !== initialValues.fullname ||
        values.contact_no !== initialValues.contact_no ||
        values.organization_name !== initialValues.organization_name
      ) {
        // Update the user's information when saving changes
        const updatedUser = {
          ...currentUser,
          ...values,
        };
        updateCurrentUser(updatedUser);
      }
      setEditable(false);
    },
  });

  return (
    <Card extra={"items-center w-full h-full bg-cover"}>
      {/* Background and profile */}
      <div
        className="relative flex h-44 w-full items-center justify-center rounded-md bg-cover"
        style={{ backgroundImage: `url(${banner})` }}
      >
        <div className="flex h-[87px] w-[87px] items-center justify-center rounded-full border border-blueSecondary bg-lightPrimary dark:!border-navy-700 dark:bg-navy-700">
          <span className="text-customBlue">
            <FaUser className="h-10 w-10" />
          </span>
        </div>
      </div>

      {/* Name and position */}
      <div className="z-10 -mt-5 grid w-full place-items-center rounded-md bg-white dark:bg-navy-700">
        <div className="mt-5 flex flex-col items-center">
          {editable ? (
            // Editable input for name
            <>
              <input
                className={`mt-1 flex h-12 w-full items-center justify-center rounded-xl border bg-white/0 p-3 text-center text-xl font-bold text-navy-700 outline-none dark:text-white ${
                  formik.touched.fullname && formik.errors.fullname
                    ? "border-red-500 text-red-500 placeholder:text-red-500 dark:!border-red-400 dark:!text-red-400 dark:placeholder:!text-red-400"
                    : ""
                }`}
                type="text"
                name="fullname"
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                value={formik.values.fullname}
                placeholder="Full Name"
              />
              {formik.touched.fullname && formik.errors.fullname && (
                <small className="ml-1.5 text-[12px] text-red-600">
                  {formik.errors.fullname}
                </small>
              )}
            </>
          ) : (
            // Display user's name
            <h4 className="text-xl font-bold text-navy-700 dark:text-white">
              {currentUser.fullname}
            </h4>
          )}
          <p className="text-base font-normal text-gray-600">
            {currentUser.email}
          </p>
        </div>

        {/* Contact Number */}
        <div className="mt-4 flex flex-col items-center justify-center">
          {editable ? (
            // Editable input for contact number
            <>
              <input
                className={`mt-1 flex h-12 w-full items-center justify-center rounded-xl border bg-white/0 p-3 text-center text-2xl font-bold text-navy-700 outline-none dark:text-white ${
                  formik.touched.contact_no && formik.errors.contact_no
                    ? "border-red-500 text-red-500 placeholder:text-red-500 dark:!border-red-400 dark:!text-red-400 dark:placeholder:!text-red-400"
                    : ""
                }`}
                type="tel"
                name="contact_no"
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                value={formik.values.contact_no}
                placeholder="Contact No."
              />
              {formik.touched.contact_no && formik.errors.contact_no && (
                <small className="ml-1.5 text-[12px] text-red-600">
                  {formik.errors.contact_no}
                </small>
              )}
            </>
          ) : (
            // Display contact number
            <p className="text-2xl font-bold text-navy-700 dark:text-white">
              {currentUser.contact_no}
            </p>
          )}
          <p className="text-sm font-normal text-gray-600">Contact No.</p>
        </div>

        {/* Organization Name */}
        <div className="mt-4 flex flex-col items-center justify-center">
          {editable ? (
            // Editable input for organization name
            <>
              <input
                className={`mt-1 flex h-12 w-full items-center justify-center rounded-xl border bg-white/0 p-3 text-center text-2xl font-bold text-navy-700 outline-none dark:text-white ${
                  formik.touched.organization_name &&
                  formik.errors.organization_name
                    ? "border-red-500 text-red-500 placeholder:text-red-500 dark:!border-red-400 dark:!text-red-400 dark:placeholder:!text-red-400"
                    : ""
                }`}
                type="text"
                name="organization_name"
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                value={formik.values.organization_name}
                placeholder="Organization"
              />
              {formik.touched.organization_name &&
                formik.errors.organization_name && (
                  <small className="ml-1.5 text-[12px] text-red-600">
                    {formik.errors.organization_name}
                  </small>
                )}
            </>
          ) : (
            // Display organization name
            <p className="text-2xl font-bold text-navy-700 dark:text-white">
              {currentUser.organization_name}
            </p>
          )}
          <p className="text-sm font-normal text-gray-600">Organization</p>
        </div>
        <span className="mt-5 self-center">
          {editable ? (
            // Save and Cancel buttons in edit mode
            <>
              <button type="submit"
                className="mr-1 w-[100px] rounded-md bg-green-500 py-1 px-2 font-semibold text-white transition-opacity hover:opacity-50"
                onClick={formik.handleSubmit}
              >
                <span className="flex items-center justify-center gap-1 text-white">
                  {<MdSave />}Save
                </span>
              </button>
              <button
                className="ml-1 w-[100px] rounded-md bg-red-500 py-1 px-2 font-semibold text-white transition-opacity hover:opacity-50"
                onClick={() => {
                  formik.resetForm();
                  setEditable(false);
                }}
              >
                <span className="flex items-center justify-center gap-1 text-white">
                  {<MdCancel />}Cancel
                </span>
              </button>
            </>
          ) : (
            // Edit button in display mode
            <button
              className="w-[100px] rounded-md bg-lightPrimary py-1 px-2 font-semibold text-white transition-opacity hover:opacity-50"
              onClick={() => setEditable(true)}
            >
              <span className="flex items-center justify-center gap-1 text-customBlue">
                {<RiEditFill />}Edit
              </span>
            </button>
          )}
        </span>
        <button
          onClick={() => logoutAction()}
          className="mt-10 mb-5 w-[100px] rounded-md border border-red-500 py-1 px-2 font-semibold text-white transition-opacity hover:opacity-50"
        >
          <span className="flex items-center justify-center gap-1 text-red-500">
            {<MdLogout />}Logout
          </span>
        </button>
      </div>
    </Card>
  );
};

export default Banner;
